<script lang="ts">
  export let text: IText;
  export let widget: IWidget;
  export let flags: IFlags;
  export let error: string;
  export let loading: boolean;
  export let done: boolean;
  export let reset: boolean = false;
  export let hideHeader: boolean = false;
  export let closable: boolean;
  export let disableScreenshot: boolean;
  export let expanded = false;
  export let screenshotLoading = false;
  export let screenshotData: string | undefined = undefined;
  export let validationError: string | undefined = undefined;
  export let onClose: () => void;
  export let onSubmit: (v: { stars?: number; optionId?: string; text?: string }) => void;
  export let onRatingOrStarsSelected: (v: { stars?: number; optionId?: string }) => void;
  export let onShowScreenshot: () => void;
  export let onScreenshot: () => void;
  export let onClearScreenshot: () => void;

  import Button from './components/button.svelte';
  import Done from './components/done.svelte';
  import Switch from './components/switch.svelte';
  import Header from './components/header.svelte';
  import Emoji from './components/emoji.svelte';
  import Row from './components/row.svelte';
  import Wrapper from './components/wrapper.svelte';
  import Hideable from './components/hideable.svelte';
  import Textarea from './components/textarea.svelte';
  import Star from './components/star.svelte';
  import PoweredBy from './components/poweredBy.svelte';
  import Error from './components/error.svelte';
  import Loading from './components/loading.svelte';
  import Screenshot from './components/screenshot.svelte';
  import ScreenshotIcon from './icons/screenshot.svelte';

  import type { IFlags, IWidget } from './interfaces/data';
  import type { IText } from './interfaces/text';
  import ValidationError from './components/validationError.svelte';

  let selectedStar: number;
  let previewSelectedStar: number;
  let selectedRating: string;
  let value = '';

  let ratingOrStarsSelected = () => {
    validationError = undefined;

    onRatingOrStarsSelected({
      stars: typeof selectedStar == 'number' ? selectedStar + 1 : undefined,
      optionId: selectedRating
    });
  };

  let submit = () => {
    if (!selectedRating && typeof selectedStar != 'number') {
      validationError = 'Please select a rating.';

      return;
    }

    validationError = undefined;

    onSubmit({
      stars: typeof selectedStar == 'number' ? selectedStar + 1 : undefined,
      optionId: selectedRating,
      text: value
    });
  };

  $: {
    if (reset) {
      reset = false;

      selectedStar = undefined;
      previewSelectedStar = undefined;
      selectedRating = undefined;
      value = '';
    }
  }
</script>

<Wrapper>
  <Loading loading={!widget && !error} />

  {#if error}
    <div style="height: 120px; margin-bottom: 3px;">
      <Error {error} />
    </div>
  {:else if widget}
    {#if !hideHeader}
      <Header name={widget.question} on:close={onClose} {closable} />
    {/if}

    <Switch showAlt={done}>
      <main
        slot="main"
        style="padding: 10px 20px 0px 20px;"
        on:mouseleave={() => (previewSelectedStar = undefined)}
      >
        {#if widget.type == 'options'}
          <Row cols={widget.options.length}>
            {#if widget.options.length == 0}
              <p>No options.</p>
            {/if}

            {#each widget.options as option}
              <Emoji
                emoji={option.emoji}
                name={option.name}
                on:click={() => {
                  if (loading) return;

                  selectedRating = option.id;
                  ratingOrStarsSelected();
                }}
                unselected={selectedRating && selectedRating != option.id}
                selected={selectedRating && selectedRating == option.id}
              />
            {/each}
          </Row>
        {/if}

        {#if widget.type == 'stars'}
          <div style="height: 8px" />

          <Row cols={widget.stars}>
            {#each new Array(widget.stars).fill(1) as _, index}
              <Star
                {index}
                total={widget.stars}
                selected={(typeof selectedStar == 'number'
                  ? selectedStar
                  : previewSelectedStar) >= index}
                on:click={() => {
                  if (loading) return;

                  selectedStar = index;
                  ratingOrStarsSelected();
                }}
                on:hover={() => (previewSelectedStar = index)}
              />
            {/each}
          </Row>

          <div style="height: 5px" />
        {/if}

        <Hideable
          disabled={expanded}
          hidden={!selectedRating && typeof selectedStar != 'number' && !expanded}
        >
          <div style="height: 10px" />

          <form on:submit|preventDefault={() => submit()}>
            <Textarea placeholder={text.moreDetails} bind:value disabled={loading} />

            {#if validationError}
              <ValidationError error={validationError} />
            {/if}

            <div style="height: 10px" />

            {#if screenshotData}
              <Screenshot
                screenshot={screenshotData}
                on:clear={onClearScreenshot}
                on:open={onShowScreenshot}
              />

              <div style="height: 10px" />
            {/if}

            <div
              style={flags && flags.screenshot && !disableScreenshot
                ? 'display: grid; grid-template-columns: 36px auto; gap: 6px'
                : ''}
            >
              {#if flags && flags.screenshot && !disableScreenshot}
                <Button
                  type="button"
                  label={text.takeScreenshot}
                  on:click={onScreenshot}
                  loading={screenshotLoading}
                >
                  <ScreenshotIcon />
                </Button>
              {/if}

              <Button type="submit" {loading} disabled={screenshotLoading}>
                {text.submit}
              </Button>
            </div>
          </form>

          <div style="height: 10px" />
        </Hideable>
      </main>

      <div slot="alt" style="height: 100%">
        <Done title={text.thanks} />
      </div>
    </Switch>
  {/if}

  {#if flags && flags.poweredBy}
    <PoweredBy text={text.poweredBy} id={widget ? widget.id : 'error'} />
  {:else}
    <div style="height: 10px" />
  {/if}
</Wrapper>
